/**
 * AssetDetailPanel — bubble-detail panel shown BELOW IndustryPipelineOverview
 * when an Asset View bubble is clicked. Backend enforces `assetId` filter
 * (CR-291101311), so the fetched detail carries only opps whose SFDC tags
 * resolve to this asset under its declared UC — no client-side filtering.
 *
 * KPI grid uses the AssetBubble props for the top row and derives influence
 * KPIs from the fetched stages (same pattern as Industry panel; single-asset
 * aggregate doesn't include per-stage counters).
 */

import type { AssetBubble } from './PipelineMap';
import {
  KPI_INFO_TIPS,
  PipelineDetailShell,
  StatCell,
  deriveKpisFromStages,
  deriveTopKpisFromDetail,
  launchRatioLabel,
} from './PipelineDetailShell';

export default function AssetDetailPanel({
  bubble,
  pipelineTypeFilter,
  pipelineFilter,
  snapshotDate,
  onClose,
}: {
  bubble: AssetBubble;
  pipelineTypeFilter?: string;
  /** Property-tree JSON forwarded so the drill-in matches the bubble filter. */
  pipelineFilter?: string;
  /** Same snapshotDate driving the chart above. */
  snapshotDate?: string;
  onClose: () => void;
}) {
  const subtitle = bubble.ucName ? `${bubble.industry} · ${bubble.ucName}` : bubble.industry;

  return (
    <PipelineDetailShell
      title={bubble.assetName}
      subtitle={subtitle}
      fetch={{ industry: bubble.industry, assetId: bubble.assetId }}
      pipelineTypeFilter={pipelineTypeFilter}
      pipelineFilter={pipelineFilter}
      snapshotDate={snapshotDate}
      showUcNameInStages
      loadingLabel={`加载 ${bubble.assetName} 明细…`}
      errorHeader="无法加载 Asset 明细"
      onClose={onClose}
      renderKpis={(detail) => {
        const kpis = deriveKpisFromStages(detail?.stages);
        // Top-half KPIs from detail response so filter changes flow through
        // (bubble is a click-time snapshot).
        const top = deriveTopKpisFromDetail(detail);
        const arrM = top ? (top.totalArr / 1e6).toFixed(2) : '—';
        // Pipeline 平均参与深度 sources from the bubble's influenceDensityOpp
        // directly so the drawer value equals the bubble chart X-axis
        // (Σ per-opp max_score ÷ opp count).
        const depth = bubble.influenceDensityOpp.toFixed(2);
        return (
          <>
            <StatCell label="Industry" value={bubble.industry} />
            <StatCell label="Opps" value={top?.oppCount ?? '—'} />
            <StatCell label="ARR" value={top ? `$${arrM}M` : '—'} />
            <StatCell
              label="Launch Ratio"
              value={top ? launchRatioLabel(top.launchedArr, top.totalArr) : '—'}
            />
            <StatCell
              label="Pipeline平均参与深度"
              value={depth}
              info={KPI_INFO_TIPS.pipelineDepth}
            />
            <StatCell
              label="渗透率"
              value={kpis.penetrationPct}
              info={KPI_INFO_TIPS.penetration}
            />
            <StatCell
              label="强相关opps数量"
              value={kpis.stronglyTouchedOpps}
              info={KPI_INFO_TIPS.stronglyTouchedOpps}
            />
            <StatCell
              label="IVT相关opps数量"
              value={kpis.touchedOpps}
              info={KPI_INFO_TIPS.touchedOpps}
            />
          </>
        );
      }}
    />
  );
}
